import type { CSSProperties } from "react";
import { Icon } from "./Icon";

interface SectionInfo {
  sectionId: string;
  startGroupIndex: number;
  groupCount: number;
  label?: string;
  icon?: string;
}

interface ProgressBarsProps {
  sections: SectionInfo[];
  currentGroupIndex: number;
  onDotClick: (index: number) => void;
  /**
   * Force the labels open regardless of pointer position (the brief reveal
   * after backward navigation). Hover and keyboard focus reveal them too,
   * but those are resolved in CSS — see the note on the root element.
   */
  showLabels?: boolean;
}

export function ProgressBars({
  sections,
  currentGroupIndex,
  onDotClick,
  showLabels = false,
}: ProgressBarsProps) {
  // Find which section is currently active
  const currentSectionIndex = sections.findIndex(
    (section) =>
      currentGroupIndex >= section.startGroupIndex &&
      currentGroupIndex < section.startGroupIndex + section.groupCount,
  );

  // Base height per group (in pixels)
  const heightPerGroup = 12;
  const gapBetweenBars = 8;

  return (
    // The menu must never cover narrative content it isn't painting, or clicks
    // and wheel events aimed at the story get swallowed by empty space. Rather
    // than gate a fixed-width overlay on state, the reveal is expressed so the
    // hit area *is* the painted area at every frame:
    //
    //   - this root is inert; only the rows below take pointer input
    //   - each row shrink-wraps its content, so a closed row is just the bar
    //   - the label column animates 0fr -> 1fr, i.e. to the label's own width,
    //     so row and label stay in lockstep with no menu width to guess at
    //
    // Reveal state and its choreography live in CSS (`[data-progress-menu]` in
    // index.css), keyed off `data-open`, `:hover` and `:focus-visible`. Keeping
    // hover out of React means it cannot latch open on an unrelated re-render,
    // and keyboard focus reveals the labels for free.
    <div
      data-progress-menu
      data-open={showLabels ? "" : undefined}
      className="pointer-events-none flex flex-col items-end"
    >
      {sections.map((section, i) => {
        const barHeight = section.groupCount * heightPerGroup;
        const isActive = i === currentSectionIndex;

        return (
          <button
            key={section.sectionId}
            onClick={() => onDotClick(section.startGroupIndex)}
            // `pointer-events-auto` re-enables input on the row alone. The
            // vertical padding replaces the old container `gap` so adjacent
            // rows touch and there is no dead strip between them; `px-4` keeps
            // the bar 16px off the viewport edge while making the edge itself
            // part of the target. `--pm-i` is the row's place in the cascade.
            className="group/row pointer-events-auto flex cursor-pointer items-center justify-end px-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/70"
            aria-label={`Go to ${section.label || section.sectionId}`}
            style={
              {
                paddingBlock: `${gapBetweenBars / 2}px`,
                "--pm-i": i,
              } as CSSProperties
            }
          >
            {section.label && (
              <span className="pm-col hidden sm:grid">
                <span
                  data-section-label
                  className={`pm-label block min-w-0 max-w-[40vw] overflow-hidden text-xs ${
                    isActive
                      ? "text-white font-medium"
                      : "text-white/60 group-hover/row:text-white"
                  }`}
                  style={{
                    fontFamily: "var(--font-narrative-body)",
                    fontWeight:
                      "var(--font-narrative-body-weight)" as unknown as number,
                  }}
                >
                  {/* The gap to the bar lives here, inside the clip, not on
                      the element above. `overflow-hidden` zeroes a grid
                      item's content minimum but never its padding, so
                      spacing on the clip itself would hold the collapsed
                      track — and the closed row's hit area — that many px
                      wide over the narrative. */}
                  <span className="flex min-w-0 items-center gap-2 whitespace-nowrap pr-2.5">
                    <Icon
                      icon={section.icon}
                      className={`pm-icon ${
                        isActive
                          ? "text-white/80"
                          : "text-white/40 group-hover/row:text-white/60"
                      }`}
                    />
                    <span className="truncate">{section.label}</span>
                  </span>
                </span>
              </span>
            )}

            <div
              className={`pm-bar w-2 rounded-full flex-shrink-0 ${
                isActive
                  ? "bg-gray-900 sm:bg-white"
                  : "bg-white/40 group-hover/row:bg-white/60"
              }`}
              style={{ height: `${barHeight}px` }}
            />
          </button>
        );
      })}
    </div>
  );
}
